import { useCallback } from "react";

import { useGetDeepestSelectionTarget } from "./useGetDeepestSelectionTarget.ts";
import { useGetNodeRoutePath } from "./useGetNodeRoutePath.ts";

/**
 * Reaching the element itself: the page that shows it, with the nearest node in its line that can
 * hold the properties panel selected on arrival.
 */
export interface SelectedOnPageLink {
  kind: "selectedOnPage";
  path: string;
  selectNodeId: number;
}

/**
 * Reaching only the page. Nothing in the node's line has standing of its own — it is rendered
 * inside another node's properties view — so there is nothing to select once there.
 */
export interface PageLink {
  kind: "page";
  path: string;
}

export type NodeLink = SelectedOnPageLink | PageLink;

/**
 * Where following a node's name would land the author, or `undefined` where it would land nowhere.
 *
 * Kept here rather than at the call site so that resolving a node to what can actually hold the
 * panel stays one concern: UI asks for a link, renders a name plainly when there is none, and hands
 * whatever it got to `useFollowNodeLink`.
 */
export const useGetNodeLink = () => {
  const getDeepestSelectionTarget = useGetDeepestSelectionTarget();
  const getNodeRoutePath = useGetNodeRoutePath();

  return useCallback(
    (nodeId: number): NodeLink | undefined => {
      const selectNodeId = getDeepestSelectionTarget(nodeId);
      if (selectNodeId !== undefined) {
        return { kind: "selectedOnPage", path: getNodeRoutePath(selectNodeId), selectNodeId };
      }
      // No selection target anywhere in the line, but the page holding it is still worth reaching.
      const path = getNodeRoutePath(nodeId);
      return path ? { kind: "page", path } : undefined;
    },
    [getDeepestSelectionTarget, getNodeRoutePath],
  );
};
